import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Helmet } from 'react-helmet';
import { AuthProvider } from './contexts/AuthContext';

// Theme
import theme from './theme/theme';

// Layout Components
import AdminLayout from './components/layout/AdminLayout';
import PublicHeader from './components/layout/PublicHeader';
import Footer from './components/layout/Footer';
import ToastProvider from './components/common/ToastProvider';

// Components
import PrivateRoute from './components/common/PrivateRoute';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';

// Public Event Pages
import EventListing from './pages/public/EventListing';
import EventDetailPublic from './pages/public/EventDetailPublic';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import Events from './pages/admin/Events';
import CreateEvent from './pages/admin/CreateEvent';
import EditEvent from './pages/admin/EditEvent';
import EventDetail from './pages/admin/EventDetail';
import Attendees from './pages/admin/Attendees';
import Users from './pages/admin/Users';
import CreateEditUser from './pages/admin/CreateEditUser';

// Usher Pages
import UsherDashboard from './pages/usher/Dashboard';
import QRScan from './pages/usher/QRScan';

// User Pages
import MyRSVPs from './pages/MyRSVPs';
import CheckIn from './pages/CheckIn';

// Public Layout Wrapper
const PublicLayout = ({ children, showFooter = true }) => (
  <>
    <PublicHeader />
    {children}
    {showFooter && <Footer />}
  </>
);

// Admin Layout Wrapper
const AdminLayoutWrapper = ({ children }) => (
  <AdminLayout>{children}</AdminLayout>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Helmet>
        <title>9DesignEvents - Event Management & Attendance System</title>
        <meta name="description" content="Complete event management and attendance tracking solution" />
      </Helmet>
      <AuthProvider>
        <Router>
          <ToastProvider />
          <Routes>
            {/* Public Routes with Layout */}
            <Route path="/" element={<PublicLayout showFooter={false}><Home /></PublicLayout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Public Event Routes with Layout */}
            <Route path="/events" element={<PublicLayout><EventListing /></PublicLayout>} />
            <Route path="/events/:slug" element={<PublicLayout><EventDetailPublic /></PublicLayout>} />

            {/* Standalone Event Registration (No Header/Footer) */}
            <Route path="/register/:slug" element={<EventDetailPublic />} />

            {/* Public Check-in Route */}
            <Route path="/checkin/:token" element={<CheckIn />} />

            {/* Authenticated User Routes - Any logged in user */}
            <Route element={<PrivateRoute roles={['super_admin', 'event_admin', 'usher']} />}>
              <Route path="/my-rsvps" element={<PublicLayout><MyRSVPs /></PublicLayout>} />
            </Route>

            {/* Admin Routes - Protected with Admin Layout */}
            <Route element={<PrivateRoute roles={['super_admin', 'event_admin']} />}>
              <Route path="/admin/dashboard" element={<AdminLayoutWrapper><AdminDashboard /></AdminLayoutWrapper>} />
              <Route path="/admin/events" element={<AdminLayoutWrapper><Events /></AdminLayoutWrapper>} />
              <Route path="/admin/events/create" element={<AdminLayoutWrapper><CreateEvent /></AdminLayoutWrapper>} />
              <Route path="/admin/events/:id" element={<AdminLayoutWrapper><EventDetail /></AdminLayoutWrapper>} />
              <Route path="/admin/events/:id/edit" element={<AdminLayoutWrapper><EditEvent /></AdminLayoutWrapper>} />
              <Route path="/admin/events/:eventId/attendees" element={<AdminLayoutWrapper><Attendees /></AdminLayoutWrapper>} />
              <Route path="/admin/qr-scan" element={<AdminLayoutWrapper><QRScan /></AdminLayoutWrapper>} />
            </Route>

            {/* User Management Routes - Super Admin Only with Admin Layout */}
            <Route element={<PrivateRoute roles={['super_admin']} />}>
              <Route path="/admin/users" element={<AdminLayoutWrapper><Users /></AdminLayoutWrapper>} />
              <Route path="/admin/users/create" element={<AdminLayoutWrapper><CreateEditUser /></AdminLayoutWrapper>} />
              <Route path="/admin/users/:id/edit" element={<AdminLayoutWrapper><CreateEditUser /></AdminLayoutWrapper>} />
            </Route>

            {/* Usher Routes - Protected with Admin Layout */}
            <Route element={<PrivateRoute roles={['usher', 'super_admin', 'event_admin']} />}>
              <Route path="/usher/dashboard" element={<AdminLayoutWrapper><UsherDashboard /></AdminLayoutWrapper>} />
              <Route path="/usher/qr-scan" element={<AdminLayoutWrapper><QRScan /></AdminLayoutWrapper>} />
            </Route>

            {/* Redirect /admin and /usher to their dashboards */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/usher" element={<Navigate to="/usher/dashboard" replace />} />

            {/* 404 - Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
